import { useEffect, useState } from "react";

import { CategoryHeader } from "../components/CategoryHeader";
import { OutputProducts } from "../components/OutputProducts";
import { PageSeo } from "../components/PageSeo";
import { useCategoriesStore } from "../stores/CategoriesStore";
import { useHotelStore } from "../stores/HotelStore";
import { countries } from "../data/countries.json";

import "../assets/scss/pages/_category.scss";

const hasWindow = typeof window !== "undefined";
if (hasWindow) {
	window.dataLayer = window.dataLayer || [];
}

const GA4ProductLists = (products, category) => {
	if (!hasWindow) return;
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: "view_item_list",
		ecommerce: {
			impressions: products.map((product, index) => ({
				item_name: product.products_name,
				item_id: product.products_id,
				item_sku: product.sku,
				price: product.price_retail,
				item_category: category,
				item_list_name: category,
				index: index + 1,
				currency: "GBP",
			})),
		},
	});
};

export const Category = ({ id, initialCategory = null, initialStructuredData = null, prefetchedProducts = [], prefetchedAvailableTags = null }) => {
	const { categoryInfo, structuredData, sort, SetCurrentInternationalCategory } = useCategoriesStore(({ categoryInfo, structuredData, sort, SetCurrentInternationalCategory }) => ({
		categoryInfo,
		structuredData,
		sort,
		SetCurrentInternationalCategory,
	}));
	const [isLoaded, setIsLoaded] = useState(Boolean(initialCategory?.content_id));

	const { setHotelFlow } = useHotelStore(({ setHotelFlow }) => ({ setHotelFlow }));

	useEffect(() => {
		setHotelFlow(false);
	}, [setHotelFlow]);

	useEffect(() => {
		if (!id) return;
		if (initialCategory?.content_id && initialCategory.content_id === id) {
			useCategoriesStore.setState({ categoryInfo: initialCategory, structuredData: initialStructuredData ?? {} });
			setIsLoaded(true);
			return;
		}
		if (categoryInfo?.content_id === id) {
			setIsLoaded(true);
			return;
		}
		// If data is missing, rely on loader to provide it; avoid client fetches here.
		setIsLoaded(false);
	}, [id, initialCategory?.content_id, categoryInfo?.content_id, initialStructuredData]);

	useEffect(() => {
		const resolvedCategory = categoryInfo?.content_id === id ? categoryInfo : initialCategory;
		
		if (resolvedCategory && resolvedCategory.content_id !== 0) {
			setIsLoaded(true);
		}

		const isInternationalHamperCat = resolvedCategory.name.toLowerCase().includes("christmas hampers")

		SetCurrentInternationalCategory("");

		countries.forEach((country) => {
			if (country?.has_sub_categories && resolvedCategory?.name?.toLowerCase().includes(country.name.toLowerCase()) && !isInternationalHamperCat) {
				SetCurrentInternationalCategory(country.name.toLowerCase());
			}
		});
	}, [categoryInfo, id, initialCategory]);

	useEffect(() => {
		const resolvedCategory = categoryInfo?.content_id === id ? categoryInfo : initialCategory;
		const resolvedProducts = prefetchedProducts?.length ? prefetchedProducts : (resolvedCategory?.products_in_category ?? []);

		if (!isLoaded || !resolvedCategory) return;

		try {
			GA4ProductLists(resolvedProducts, resolvedCategory.name);
		} catch (error) {
			console.error("Error setting GA4 data", error);
		}
	}, [id, isLoaded, categoryInfo, initialCategory, prefetchedProducts]);

	const categorySpecials = (id, specials = false) => {
		let specialClasses = [];
		let specialCallouts = "";

		switch (id) {
			case 155: //luxury
				// specialClasses.push("rene-category");
				// specialCallouts = "rene";
				break;
			default:
				break;
		}

		return specials !== true ? specialClasses.join(" ") : specialCallouts;
	};

	const resolvedCategory = categoryInfo?.content_id === id ? categoryInfo : initialCategory;
	const resolvedProducts = prefetchedProducts?.length ? prefetchedProducts : resolvedCategory?.products_in_category || [];

	return (
		<>
			<PageSeo
				title={resolvedCategory?.meta?.[id]?.title?.trim() || resolvedCategory?.name || "Category"}
				description={resolvedCategory?.meta?.[id]?.description?.trim() || resolvedCategory?.footer_content || ""}
				keywords={resolvedCategory?.meta?.[id]?.keywords?.trim() || ""}
				canonical={resolvedCategory?.category_url?.trim() || ""}
				structuredData={initialStructuredData ?? structuredData ?? {}}
			/>
			<div className={`category container ${resolvedCategory?.name ? resolvedCategory?.name.replaceAll(" ", "-").toLowerCase() : ""} ${sort ? sort.replaceAll(" ", "-").replaceAll(":", "").toLowerCase() : ""} ${categorySpecials(id)}`}>
				<CategoryHeader specialName={categorySpecials(id, true)} />
				{resolvedCategory?.name && <h1 className="category-title">{resolvedCategory.name}</h1>}
				<div className={`category-products`}>
					{resolvedCategory ? (
						<OutputProducts
							products={resolvedCategory?.products_in_category || []}
							prefetchedProducts={resolvedProducts}
							prefetchedAvailableTags={prefetchedAvailableTags}
							specialName={categorySpecials(id, true)}
							aboveFold={true}
							lazy={true}
							buyButton={true}
							sort={true}
						/>
					) : (
						<h1>No Products</h1>
					)}
				</div>
			</div>
		</>
	);
};

export default Category;
